import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";
import {
  CONTROL_SIZE_TOKENS,
  type ControlSizeProp,
} from "@/components/ui/control-size";

// Lets a TabsTrigger inherit its size from the surrounding TabsList when it
// doesn't set its own `size`, so the list's box and its triggers always match
// (previously, 20+ call sites set `size` only on the triggers, leaving the
// list at its default height).
const TabsSizeContext = React.createContext<ControlSizeProp | undefined>(
  undefined,
);

const tabsListVariants = cva(
  "overflow-hidden w-full flex justify-start items-center rounded-none bg-slate-200 p-0.5 text-muted-foreground",
  {
    variants: {
      size: {
        // The list's outer box uses the same height as Button/Input, so the
        // whole tab bar lines up with sibling controls of the same `size`.
        default: CONTROL_SIZE_TOKENS.md.height,
        xs: CONTROL_SIZE_TOKENS.xs.height,
        sm: CONTROL_SIZE_TOKENS.sm.height,
        md: CONTROL_SIZE_TOKENS.md.height,
        lg: CONTROL_SIZE_TOKENS.lg.height,
        xl: CONTROL_SIZE_TOKENS.xl.height,
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

// Explicit trigger height = TabsList height minus its `p-0.5` track padding
// (4px, exactly one step of the scale). An explicit height instead of
// `h-full` keeps the pill correct even when a trigger is wrapped in another
// element (e.g. TourHighlight), where `h-full` would resolve against the
// wrapper and collapse to the text height.
const TABS_TRIGGER_HEIGHT = {
  xs: "h-4 md:h-5",
  sm: "h-5 md:h-6",
  md: "h-6 md:h-7",
  lg: "h-7 md:h-8",
  xl: "h-8 md:h-9",
} as const;

const tabsTriggerVariants = cva(
  `flex
  items-center
  justify-center
  whitespace-nowrap
  rounded-md
  font-medium
  ring-offset-background
  transition-all
  focus-visible:outline-none
  focus-visible:ring-2
  focus-visible:ring-ring
  focus-visible:ring-offset-2
  disabled:pointer-events-none
  disabled:opacity-50
  data-[state=active]:bg-white
  data-[state=active]:text-foreground
  data-[state=active]:shadow-[2px_2px_6px_0px_rgba(0,0,0,0.16)]
  data-[state=active]:relative
  data-[state=active]:z-10
  [&>svg]:shrink-0
  `,
  {
    variants: {
      size: {
        default: `${TABS_TRIGGER_HEIGHT.md} ${CONTROL_SIZE_TOKENS.md.px} ${CONTROL_SIZE_TOKENS.md.text} ${CONTROL_SIZE_TOKENS.md.icon}`,
        xs: `${TABS_TRIGGER_HEIGHT.xs} ${CONTROL_SIZE_TOKENS.xs.px} ${CONTROL_SIZE_TOKENS.xs.text} ${CONTROL_SIZE_TOKENS.xs.icon}`,
        sm: `${TABS_TRIGGER_HEIGHT.sm} ${CONTROL_SIZE_TOKENS.sm.px} ${CONTROL_SIZE_TOKENS.sm.text} ${CONTROL_SIZE_TOKENS.sm.icon}`,
        md: `${TABS_TRIGGER_HEIGHT.md} ${CONTROL_SIZE_TOKENS.md.px} ${CONTROL_SIZE_TOKENS.md.text} ${CONTROL_SIZE_TOKENS.md.icon}`,
        lg: `${TABS_TRIGGER_HEIGHT.lg} ${CONTROL_SIZE_TOKENS.lg.px} ${CONTROL_SIZE_TOKENS.lg.text} ${CONTROL_SIZE_TOKENS.lg.icon}`,
        xl: `${TABS_TRIGGER_HEIGHT.xl} ${CONTROL_SIZE_TOKENS.xl.px} ${CONTROL_SIZE_TOKENS.xl.text} ${CONTROL_SIZE_TOKENS.xl.icon}`,
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Root ref={ref} className={cn(className)} {...props} />
));

Tabs.displayName = "Tabs";

export interface TabsListProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
> {
  size?: ControlSizeProp;
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, size, ...props }, ref) => (
  <TabsSizeContext.Provider value={size}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ size }), "rounded-md", className)}
      {...props}
    />
  </TabsSizeContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

export interface TabsTriggerProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
> {
  size?: ControlSizeProp;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, size, ...props }, ref) => {
  const listSize = React.useContext(TabsSizeContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ size: size ?? listSize }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export {
  Tabs,
  TabsContent,
  TabsList,
  tabsListVariants,
  TabsTrigger,
  tabsTriggerVariants,
};
